import {inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {
  CompanyControllerService,
  CompanyNameCheckResponse,
  CompanyResponse,
  CreateUpdateCompanyRequest,
} from '@frontend/shared-api';
import {WizardService} from '../../../../shared/components/wizard/wizard.service';
import {
  City,
  CityStepData,
  CompanyStepData,
  CountryStepData,
  isValidCompanyStepData,
  OfficeStepData,
} from './company-wizard.types';

@Injectable({
  providedIn: 'root',
})
export class CompanyWizardService extends WizardService<CreateUpdateCompanyRequest> {
  private readonly companyControllerService = inject(CompanyControllerService);
  private readonly COMPANY_STEP = 1;
  private readonly COUNTRY_STEP = 2;
  private readonly CITY_STEP = 3;
  private readonly OFFICE_STEP = 4;

  constructor() {
    super();
  }

  checkCompanyNameExists(name: string): Observable<CompanyNameCheckResponse> {
    if (!name?.trim()) {
      throw new Error('Company name is required');
    }

    return this.companyControllerService.checkCompanyNameExists({name}).pipe(
      catchError((error) => {
        console.error('Error checking company name:', error);
        throw error;
      }),
    );
  }

  protected buildPartialDTO(): void {
    try {
      const dto: Partial<CreateUpdateCompanyRequest> = {};
      this.processCompanyStep(dto);
      this.processCountryStep(dto);
      this.processCityStep(dto);
      this.processOfficeStep(dto);
      this.updateFormData(dto);
    } catch (error) {
      console.error('Error building partial DTO:', error);
      throw error;
    }
  }

  private processCompanyStep(dto: Partial<CreateUpdateCompanyRequest>): void {
    const companyData = this.getStepData<CompanyStepData>(this.COMPANY_STEP);
    if (isValidCompanyStepData(companyData)) {
      dto.company = companyData.company;
      dto.website = companyData.website;
      dto.street = companyData.street;
      dto.zip = companyData.zip;
      dto.city = companyData.city;
    }
  }

  private processCountryStep(dto: Partial<CreateUpdateCompanyRequest>): void {
    const countriesData = this.getStepData<CountryStepData>(this.COUNTRY_STEP);
    if (countriesData?.countries) {
      dto.countries = countriesData.countries.map((country) => ({
        countryCode: country.countryCode,
        cities: [],
      }));
    }
  }

  private processCityStep(dto: Partial<CreateUpdateCompanyRequest>): void {
    const citiesData = this.getStepData<CityStepData>(this.CITY_STEP);
    if (citiesData?.cities && dto.countries) {
      dto.countries.forEach((country) => {
        country.cities = citiesData.cities[country.countryCode] || [];
      });
    }
  }

  private processOfficeStep(dto: Partial<CreateUpdateCompanyRequest>): void {
    const officesData = this.getStepData<OfficeStepData>(this.OFFICE_STEP);
    if (officesData?.offices && dto.countries) {
      dto.countries.forEach((country) => {
        country.cities?.forEach((city) => {
          city.offices = officesData.offices[(city as City).uuid] || [];
        });
      });
    }
  }

  override buildFinalRequest(): CreateUpdateCompanyRequest {
    const data = this.formData;
    const countries = (data.countries || []).map((country) => ({
      countryCode: country.countryCode,
      cities: (country.cities || []).map((city) => ({
        name: city.name,
        offices: city.offices,
      })),
    }));

    return {
      company: data.company || '',
      website: data.website || '',
      street: data.street || '',
      zip: data.zip || '',
      city: data.city || '',
      countries,
    };
  }

  createCompany(request: CreateUpdateCompanyRequest): Observable<any> {
    if (!request?.company) {
      throw new Error('Invalid company request');
    }

    return this.companyControllerService
      .createCompany({
        createUpdateCompanyRequest: request,
      })
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) => {
          console.error('Error creating company:', error);
          throw error;
        }),
      );
  }

  updateCompany(request: CreateUpdateCompanyRequest): Observable<any> {
    if (!request?.company) {
      throw new Error('Invalid company request');
    }

    const companyId = this.isEdit()
      ? this.getStepData<CompanyStepData>(this.COMPANY_STEP)?.id
      : null;

    if (!companyId) {
      throw new Error('Company ID is required for update');
    }

    return this.companyControllerService
      .updateCompany({
        id: companyId,
        createUpdateCompanyRequest: request,
      })
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) => {
          console.error('Error creating company:', error);
          throw error;
        }),
      );
  }

  getCompanyData(id: number): Observable<CompanyResponse> {
    if (!id) {
      throw new Error('Company id is required');
    }

    return this.companyControllerService.getCompanyData({id}).pipe(
      catchError((error) => {
        console.error('Error fetching company data:', error);
        throw error;
      }),
    );
  }
}
